// src/lib/stock-rythme.js — LE RYTHME PRÉDIT (refonte Stock, Dimitri 2026-08-21).
//
// « Six capsules par semaine, dernier dépôt il y a dix jours » — C'EST UNE
// PROBABILITÉ, jamais un fait : la carte qui la porte est AMBRE avec l'icône
// courbe, et ne se mélange jamais au signalement d'un agent (terracotta, main).
//
// Helpers PURS : les dépôts et la date du jour sont INJECTÉS — aucun accès
// réseau, aucune horloge interne. Le calcul se nourrit de l'HISTORIQUE des
// dépôts (fbz8-depots-v2 : lignes [{csId, nbPrelevables}] datées par logement),
// données qui existaient déjà sans être exploitées.
//
// PRUDENCE ASSUMÉE (une prédiction fausse coûte plus qu'une absence) :
//   · moins de 3 dépôts d'un produit sur la fenêtre → PAS de rythme (null) ;
//   · fenêtre = 56 jours (8 semaines) — assez pour lisser, assez court pour
//     suivre une saison ;
//   · le rythme est GLOBAL (tous logements) : c'est le central qui se vide.

export const FENETRE_RYTHME_JOURS = 56
export const MIN_DEPOTS_POUR_RYTHME = 3

function joursEntre(isoA, isoB) {
  const a = Date.parse(`${String(isoA).slice(0, 10)}T12:00:00Z`)
  const b = Date.parse(`${String(isoB).slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(a) || Number.isNaN(b)) return null
  return Math.round((b - a) / 86400000)
}

/**
 * Le rythme de consommation d'UN produit — unités par semaine, sur la fenêtre.
 * @param {Array} depots — [{date: 'YYYY-MM-DD', lignes: [{csId, nbPrelevables}]}]
 * @param {string} csId
 * @param {string} aujourdHuiIso — injecté (Europe/Paris).
 * @returns {{ parSemaine: number, nbDepots: number, dernierDepotIso: string }|null}
 */
export function rythmeProduit(depots, csId, aujourdHuiIso) {
  const pertinents = []
  for (const d of Array.isArray(depots) ? depots : []) {
    const anciennete = joursEntre(d?.date, aujourdHuiIso)
    if (anciennete == null || anciennete < 0 || anciennete > FENETRE_RYTHME_JOURS) continue
    const n = (Array.isArray(d?.lignes) ? d.lignes : [])
      .filter((l) => l?.csId === csId)
      .reduce((s, l) => s + (Number(l?.nbPrelevables) || 0), 0)
    if (n > 0) pertinents.push({ date: String(d.date).slice(0, 10), n })
  }
  if (pertinents.length < MIN_DEPOTS_POUR_RYTHME) return null
  const total = pertinents.reduce((s, x) => s + x.n, 0)
  const dates = pertinents.map((x) => x.date).sort()
  const dernierDepotIso = dates[dates.length - 1]
  // La période observée : du premier dépôt de la fenêtre à AUJOURD'HUI (pas au
  // dernier dépôt — le silence depuis compte comme du temps de consommation).
  const periodeJours = Math.max(7, joursEntre(dates[0], aujourdHuiIso))
  return {
    parSemaine: Math.round((total / periodeJours) * 7 * 10) / 10,
    nbDepots: pertinents.length,
    dernierDepotIso,
  }
}

/**
 * « Il en reste N jours au rythme habituel » — null si pas de rythme fiable
 * ou stock nul sans rythme.
 * @returns {{ joursRestants: number, parSemaine: number }|null}
 */
export function joursRestants(qtyDispo, depots, csId, aujourdHuiIso) {
  const r = rythmeProduit(depots, csId, aujourdHuiIso)
  if (!r || r.parSemaine <= 0) return null
  const parJour = r.parSemaine / 7
  return {
    joursRestants: Math.max(0, Math.round((Number(qtyDispo) || 0) / parJour)),
    parSemaine: r.parSemaine,
  }
}

/** Les produits « bientôt finis » : jours restants ≤ seuilJours (défaut 7). */
export function produitsBientotFinis(stockCentral, depots, aujourdHuiIso, seuilJours = 7) {
  const sortie = []
  for (const p of Array.isArray(stockCentral) ? stockCentral : []) {
    const r = joursRestants(p?.qtyDispo, depots, p?.csId, aujourdHuiIso)
    if (r && r.joursRestants <= seuilJours) {
      sortie.push({ csId: p.csId, joursRestants: r.joursRestants, parSemaine: r.parSemaine })
    }
  }
  return sortie.sort((a, b) => a.joursRestants - b.joursRestants)
}

/**
 * LE LOGEMENT EST-IL DÛ POUR UN DÉPÔT ? (vue « Les logements » — PRÉDICTION,
 * carte ambre, jamais mélangée au signalement). Rythme PAR LOGEMENT : les
 * écarts entre dépôts de CE produit dans CE logement — si le silence depuis le
 * dernier dépôt dépasse l'intervalle habituel, c'est le moment.
 * Prudence identique : ≥ 3 dépôts sur la fenêtre, sinon aucun chiffre.
 * @returns {Array<{csId, joursDepuis, intervalleJours}>}
 */
export function predictionsLogement(depots, logId, aujourdHuiIso) {
  const duLogement = (Array.isArray(depots) ? depots : []).filter((d) => d?.logId === logId)
  const parProduit = {}
  for (const d of duLogement) {
    const anciennete = joursEntre(d?.date, aujourdHuiIso)
    if (anciennete == null || anciennete < 0 || anciennete > FENETRE_RYTHME_JOURS) continue
    for (const l of (Array.isArray(d?.lignes) ? d.lignes : [])) {
      if (!l?.csId || !(Number(l?.nbPrelevables) > 0)) continue
      ;(parProduit[l.csId] = parProduit[l.csId] || []).push(String(d.date).slice(0, 10))
    }
  }
  const dues = []
  for (const [csId, dates] of Object.entries(parProduit)) {
    const uniques = [...new Set(dates)].sort()
    if (uniques.length < MIN_DEPOTS_POUR_RYTHME) continue
    let somme = 0
    for (let i = 1; i < uniques.length; i++) somme += joursEntre(uniques[i - 1], uniques[i])
    const intervalleJours = Math.max(1, Math.round(somme / (uniques.length - 1)))
    const joursDepuis = joursEntre(uniques[uniques.length - 1], aujourdHuiIso)
    if (joursDepuis != null && joursDepuis >= intervalleJours) {
      dues.push({ csId, joursDepuis, intervalleJours })
    }
  }
  return dues.sort((a, b) => (b.joursDepuis - b.intervalleJours) - (a.joursDepuis - a.intervalleJours))
}
